import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readSlideMasters } from "@/office/powerpoint/design";
import {
  allShapes,
  freeTextBoxes,
  hasTitleText,
  longestParagraphWords,
  readPresentation,
} from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionKursabschluss: Lesson = {
  id: "kursabschluss",
  title: "Abschlussübung des Kurses",
  summary: "Eine Präsentation, die alle roten Fäden dieses Kurses erfüllt.",
  durationMinutes: 12,
  learningGoals: [
    "Eine vollständige Präsentation nach allen Regeln des Kurses aufbauen",
    "Die eigene Arbeit vor der Weitergabe strukturell prüfen",
  ],
  steps: [
    {
      id: "rueckblick",
      title: "Die beiden roten Fäden",
      explanation: [
        "Der Kurs hatte zwei Gedanken, die sich durch alle Module gezogen haben.",
        "Der erste: Layouts und Platzhalter statt frei gezogener Textfelder. Eine Folie, " +
          "die über ihr Layout aufgebaut ist, erbt Position, Schriftgröße und Farbe – und " +
          "übersteht deshalb jeden Designwechsel, jeden Layoutwechsel und jede " +
          "Weitergabe. Sie erscheint in der Gliederung, im Export und beim Vorlesen " +
          "durch einen Screenreader. Eine Folie aus frei gezogenen Kästen tut nichts " +
          "davon.",
        "Der zweite: Eine Folie ist kein Dokument. Auf die Folie kommt, was das " +
          "gesprochene Wort nicht leisten kann – Zahlen, Bilder, Struktur. Alles andere " +
          "wird gesagt und steht in den Sprechernotizen. Wer das trennt, bekommt beides: " +
          "einen Vortrag, dem man folgen kann, und eine Nachlese, die vollständig ist.",
        "Beide Gedanken haben dasselbe Muster wie der Word-Kurs: Bedeutung und Aussehen " +
          "sind zwei verschiedene Dinge, und für jede Behelfslösung gibt es die richtige " +
          "Einstellung.",
      ],
      keyPoints: [
        "Layouts und Platzhalter statt frei gezogener Textfelder.",
        "Eine Folie ist kein Dokument – Stichpunkte statt Fließtext.",
        "Bedeutung vor Aussehen, wie im Word-Kurs.",
      ],
    },
    {
      id: "abschluss",
      title: "Abschlussübung",
      explanation: [
        "Die letzte Übung geht über die ganze Präsentation. Sechs Punkte werden geprüft " +
          "– sie fassen zusammen, worum es in diesem Kurs ging.",
      ],
      keyPoints: [
        "Gliederungsansicht für Titel und Text, Auswahlbereich für die Objekte.",
        "„Start“ → „Zurücksetzen“ richtet die Platzhalter am Layout aus.",
        "„Überprüfen“ → „Barrierefreiheit überprüfen“ findet den Rest.",
      ],
      task: {
        instruction:
          "Deine Präsentation soll: mindestens sechs Folien haben, auf jeder Folie einen " +
          "gefüllten Titel tragen, ohne frei gezogene Textfelder auskommen, keine " +
          "Stichpunkte über zehn Wörter enthalten, mit genau einem Folienmaster arbeiten " +
          "und für jedes Bild einen Alternativtext haben.",
        hint: "Die ersten drei Punkte siehst du in Gliederungsansicht und Auswahlbereich, den Rest in der Barrierefreiheitsprüfung.",
        check: async () => {
          const snapshot = await readPresentation();
          const master = await readSlideMasters();

          if (!snapshot.features.shapeDetails || master === null) {
            return nichtPruefbar(
              "Formen, Platzhalter und Folienmaster",
              "1.4",
              "Geh die sechs Punkte selbst durch – Gliederungsansicht, Auswahlbereich und Barrierefreiheitsprüfung zeigen alle davon.",
            );
          }
          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Bau zuerst eine Präsentation auf – die Module dieses Kurses zeigen, wie.",
            );
          }

          const anzahl = snapshot.slides.length;
          const ohneTitel = snapshot.slides.filter((folie) => !hasTitleText(folie));
          const textfelder = snapshot.slides.flatMap(freeTextBoxes);
          const zuLang = allShapes(snapshot).filter(
            (form) => form.type === "Placeholder" && longestParagraphWords(form) > 10,
          );
          const bildhaft = allShapes(snapshot).filter(
            (form) =>
              form.type === "Image" ||
              form.type === "Chart" ||
              form.type === "SmartArt" ||
              form.placeholder?.containedType === "Image" ||
              form.placeholder?.containedType === "Chart",
          );
          const ohneAltText = bildhaft.filter(
            (form) => (form.altText ?? "").trim().length === 0 && form.isDecorative !== true,
          );

          const kriterien = [
            criterion(`Mindestens sechs Folien (gefunden: ${anzahl})`, anzahl >= 6),
            criterion(
              ohneTitel.length === 0
                ? "Jede Folie hat einen gefüllten Titel"
                : `Ohne Titel: Folie ${ohneTitel.map((folie) => folie.index + 1).join(", ")}`,
              ohneTitel.length === 0,
            ),
            criterion(
              textfelder.length === 0
                ? "Kein frei gezogenes Textfeld"
                : `Noch ${textfelder.length} freie Textfelder, zuerst auf Folie ${textfelder[0].slideIndex + 1}`,
              textfelder.length === 0,
            ),
            criterion(
              zuLang.length === 0
                ? "Kein Stichpunkt länger als zehn Wörter"
                : `Zu langer Stichpunkt auf Folie ${zuLang[0].slideIndex + 1}`,
              zuLang.length === 0,
            ),
            criterion(`Genau ein Folienmaster (gefunden: ${master.length})`, master.length === 1),
          ];

          // Alternativtexte gibt es erst ab PowerPointApi 1.10. Fehlen sie,
          // wird der Punkt nicht stillschweigend als erfüllt gewertet, sondern
          // aus der Liste genommen und am Ende offen benannt.
          if (snapshot.features.altText) {
            kriterien.push(
              criterion(
                bildhaft.length === 0
                  ? "Kein Bild vorhanden, das einen Alternativtext bräuchte"
                  : ohneAltText.length === 0
                    ? `Alle ${bildhaft.length} Bilder und Diagramme haben einen Alternativtext`
                    : `Ohne Alternativtext: ${ohneAltText.length} Objekte, zuerst auf Folie ${ohneAltText[0].slideIndex + 1}`,
                ohneAltText.length === 0,
              ),
            );
          } else if (kriterien.every((eintrag) => eintrag.passed)) {
            return nichtPruefbar(
              "Der Alternativtext einer Form",
              "1.10",
              "Die ersten fünf Punkte sind erfüllt. Den sechsten – Alternativtexte für alle Bilder und " +
                "Diagramme – kontrolliere über „Überprüfen“ → „Barrierefreiheit überprüfen“.",
            );
          }

          return fromCriteria(kriterien, {
            success:
              "Die Präsentation erfüllt alle sechs Punkte – sie übersteht einen Designwechsel, " +
              "lässt sich vorlesen und trägt den Vortrag, statt ihn zu ersetzen.",
            failure: "Fast am Ziel. Diese Punkte fehlen noch:",
            hint:
              "Die meisten Punkte lassen sich in wenigen Minuten beheben: „Start“ → „Zurücksetzen“ für die " +
              "Platzhalter, Rechtsklick → „Alternativtext bearbeiten“ für die Bilder." +
              (snapshot.features.altText
                ? ""
                : " Die Alternativtexte stehen nicht in der Liste, weil diese PowerPoint-Version sie nicht " +
                  "ausliest – kontrolliere sie über „Überprüfen“ → „Barrierefreiheit überprüfen“."),
          });
        },
      },
    },
  ],
};
